import { UserSchema } from '@/entities/user/model';
import { getUser } from '@/features/auth/lib/auth.services';
import { captureSentryError } from '@/shared/lib/utils/sentry.utils';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const YMCHAT_LOADER_URL = import.meta.env.VITE_YMCHAT_LOADER_URL;
const YMCHAT_API_KEY = import.meta.env.VITE_YMCHAT_API_KEY;
const YMCHAT_EXCLUDED_PATHS = ['/store', '/branch', '/brand', '/reservation', '/questionnaire', '/payment', '/signup'];

let loadPromise: Promise<void> | null = null;

export const loadYMChatOnce = (): Promise<void> => {
  if (window.YMChatInitialized) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${YMCHAT_LOADER_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('YMChat script load failed')));
      return;
    }

    if (!window.YMChat) {
      const f: any = function (...args: any[]) {
        f.c(args);
      };
      f.q = [];
      f.c = (a: any) => f.q!.push(a);
      window.YMChat = f;
    }

    const s = document.createElement('script');
    s.async = true;
    s.src = YMCHAT_LOADER_URL;
    s.onload = () => {
      window.YMChatInitialized = true;
      resolve();
    };
    s.onerror = () => reject(new Error('YMChat script load failed'));
    document.head.appendChild(s);
  });

  return loadPromise;
};

export const useYMChat = (user: UserSchema | null): void => {
  const { pathname } = useLocation();
  const bootedRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    const excluded = YMCHAT_EXCLUDED_PATHS.some((p) => pathname.startsWith(p));
    if (excluded) {
      if (bootedRef.current) {
        window.YMChat?.('shutdown');
        bootedRef.current = false;
      }
      return;
    }

    if (bootedRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        if (!YMCHAT_LOADER_URL) throw new Error('VITE_YMCHAT_LOADER_URL is missing');
        if (!YMCHAT_API_KEY) throw new Error('VITE_YMCHAT_API_KEY is missing');

        await loadYMChatOnce();
        if (cancelled) return;

        const response = await getUser();
        const fresh = response.data.body[0];

        if (!fresh?.memberHash) {
          captureSentryError(new Error('YMChat boot: memberHash missing'), {
            tags: { feature: 'ymchat', action: 'no_hash' },
            context: { where: 'useYMChat_boot', userId: fresh?.id ?? null }
          });
          return;
        }

        const csmIdx = Number(fresh.id);
        if (Number.isNaN(csmIdx)) {
          captureSentryError(new Error('YMChat boot: invalid csmIdx'), {
            tags: { feature: 'ymchat', action: 'invalid_id' },
            context: { where: 'useYMChat_boot', rawId: fresh.id }
          });
          return;
        }

        if (cancelled) return;
        bootedRef.current = true;
        window.YMChat?.('boot', {
          apiKey: YMCHAT_API_KEY,
          member: { csmIdx, memberHash: fresh.memberHash }
        });
      } catch (error) {
        captureSentryError(error, {
          tags: { feature: 'ymchat', action: 'boot_failed' },
          context: { where: 'useYMChat_boot' }
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, pathname]);

  useEffect(() => {
    if (user?.id) return;
    if (!bootedRef.current) return;
    window.YMChat?.('shutdown');
    bootedRef.current = false;
  }, [user?.id]);
};
